import React, { useState } from 'react';
import useGeoData from '../../../store/useGeoData';
import {
  selectActiveBasemap,
  selectCurrentBackgroundColor,
  selectUseSolidBackground,
  selectCurrentBackgroundOpacity, 
} from '../../../store/basemapSlice';
import type { BasemapStyle } from '../../../store/types';
import styles from '../styles/BasemapMenu.module.css'; // Stili esistenti
import Tabs, { TabItem } from '../../../common/Tabs/Tabs';
import ToggleButton from '../../../common/inputs/ToggleButton/ToggleButton';
import StylePropertyInput from '../../../common/StylePropertyInput/StylePropertyInput'; // Importa StylePropertyInput

interface BasemapMenuProps {
}

const BasemapMenu: React.FC<BasemapMenuProps> = () => {
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});

  const availableBasemaps = useGeoData((state) => state.availableBasemaps);
  const activeBasemap = useGeoData(selectActiveBasemap);
  const activeBasemapId = activeBasemap?.id;
  const currentBackgroundColor = useGeoData(selectCurrentBackgroundColor);
  const useSolidBackground = useGeoData(selectUseSolidBackground);
  const currentBackgroundOpacity = useGeoData(selectCurrentBackgroundOpacity); // Aggiunto

  const dispatchToggleUseSolidBackground = useGeoData(state => state.toggleUseSolidBackground); // Aggiunto
  const dispatchSetActiveBackgroundColor = useGeoData(state => state.setActiveBackgroundColor); // Aggiunto
  const dispatchSetActiveBackgroundOpacity = useGeoData(state => state.setActiveBackgroundOpacity); // Aggiunto

  const vectorBasemaps = availableBasemaps.filter(b => b.type === 'vector');
  const rasterBasemaps = availableBasemaps.filter(b => b.type === 'raster');

  const handleImageError = (imageUrl: string) => {
    setImageErrors((prevErrors) => ({
      ...prevErrors,
      [imageUrl]: true,
    }));
  };

  const handleBasemapSelection = (basemapId: string) => {
    if (useSolidBackground) { // Usa la variabile di stato
      dispatchToggleUseSolidBackground();
    }
    useGeoData.getState().setBasemap(basemapId); // setBasemap non è stata esposta come azione singola, si usa getState
  };

  const renderBasemapList = (basemaps: BasemapStyle[]) => (
    <div className={styles.basemapSelectorContainer}>
      {basemaps.map((style) => {
        const hasValidImage = style.imageUrl && !imageErrors[style.imageUrl];
        const isActive = style.id === activeBasemapId && !useSolidBackground; // La basemap è attiva solo se non stiamo usando lo sfondo solido

        return (
          <button
            key={style.id}
            className={`${styles.basemapItem} ${isActive ? styles.active : ''}`}
            onClick={() => handleBasemapSelection(style.id)}
            title={style.name}
          >
            <div className={styles.thumbnailContainer}>
              {hasValidImage ? (
                <img
                  src={style.imageUrl}
                  alt={`Anteprima ${style.name}`}
                  className={styles.basemapThumbnail}
                  onError={() => style.imageUrl && handleImageError(style.imageUrl)}
                />
              ) : (
                <div className={styles.basemapPlaceholder}>
                  <span>{style.name}</span>
                </div>
              )}
            </div>
            <span className={styles.basemapName}>{style.name}</span>
          </button>
        );
      })}
    </div>
  );

  const tabItems: TabItem[] = [
    {
      label: 'Vector Tiles',
      content: renderBasemapList(vectorBasemaps),
    },
    {
      label: 'Raster Tiles',
      content: renderBasemapList(rasterBasemaps),
    },
  ];

  return (
    <div style={{ padding: 'var(--spacing-s) var(--spacing-m)' }}>
      <div style={{ marginBottom: 'var(--spacing-m)', padding: '0 var(--spacing-xs)' }}>
        <ToggleButton
          id="use-solid-background-toggle"
          isOn={useSolidBackground}
          onToggle={dispatchToggleUseSolidBackground}
          label="Enable Background"
        />

      {useSolidBackground && (
        // Aggiungo il padding che era nel div contenitore originale dei controlli
        // per mantenere la coerenza con il ToggleButton sopra, se necessario.
        // StylePropertyInput dovrebbe avere i suoi margini/padding interni.
        <div style={{ marginTop: 'var(--spacing-s)'}}>
          <StylePropertyInput
            layerId="basemap-settings-color" // ID univoco per accessibilità e chiavi React
            propertyName="backgroundColor"
            label="Color:"
            currentValue={currentBackgroundColor || '#ffffff'}
            propertyType="color"
            isExpressionPossible={false}
            onStyleChange={(_id, _prop, value) => {
              if (typeof value === 'string') {
                dispatchSetActiveBackgroundColor(value);
              }
            }}
            disabled={!useSolidBackground}
          />
          <StylePropertyInput
            layerId="basemap-settings-opacity" // ID univoco
            propertyName="backgroundOpacity"
            label="Opacity:"
            currentValue={currentBackgroundOpacity}
            propertyType="range"
            numberConfig={{ min: 0, max: 1, step: 0.01 }}
            isExpressionPossible={false}
            onStyleChange={(_id, _prop, value) => {
              if (typeof value === 'number') {
                dispatchSetActiveBackgroundOpacity(value);
              }
            }}
            disabled={!useSolidBackground}
          />
        </div>
      )}
      </div>
      <Tabs tabs={tabItems} />
    </div>
  );
};

export default BasemapMenu;